'use client';

import * as React from 'react';
import { useSessionStorageValue } from '@react-hookz/web';
import {
  allCssAtom,
  brandColorAtom,
  designAtom,
  faviconAtom,
  fontAtom,
  fontSizeAtom,
  themeAtom,
} from 'app/blog/[domain]/_components/design-state';
import { resolveTheme } from 'app/blog/[domain]/_themes/theme-registry';
import { getFontFamily } from 'app/blog/[domain]/(standalone)/_components/font-utils';
import { getColorVairables } from 'app/blog/[domain]/(standalone)/_components/layout/color-utils';
import clsx from 'clsx';
import { IconMonitor01 } from 'icon/Media&devices/monitor-01';
import { IconPhone02 } from 'icon/Media&devices/phone-02';
import { useAtom, useAtomValue } from 'jotai';
import Frame, { useFrame } from 'react-frame-component';
import { cn, FocusableLink, ToggleGroup, ToggleGroupItem } from 'ui';
import type { DesignTab } from '../design-tabs';
import { IframeStyle } from './iframe-style';
import { IframeJsRunner } from './js-runner';
import { useIframeHead } from './use-iframe-head';

export interface SafariProps {
  openUrl: string;
  urlBar: string;
  id: string;
  children: React.ReactNode;
  tab: DesignTab;
  directRender?: boolean;
  className?: string;
}

export function Safari(props: SafariProps): JSX.Element {
  const favicon = useAtomValue(faviconAtom);
  const _favicon = favicon;
  const iframeHead = useIframeHead();
  const defaultFont = useAtomValue(fontAtom);
  const fontSize = useAtomValue(fontSizeAtom);
  const brandColor = useAtomValue(brandColorAtom);
  const allCss = useAtomValue(allCssAtom);
  const { value: device, set: setDevice } = useSessionStorageValue<
    'mobile' | 'desktop'
  >('device', {
    defaultValue: 'desktop',
  });
  const handleDeviceChange = (value: 'mobile' | 'desktop') => {
    setDevice(value);
  };
  const theme = useAtomValue(themeAtom);
  const themeDefinition = resolveTheme(theme);
  const isDark = themeDefinition.colorScheme === 'dark';
  return (
    <section
      className={cn(
        'mx-auto flex h-full min-h-[1000px] max-w-[1280px] flex-col rounded-xl border border-gray-300 bg-white md:min-h-[600px]',
        device === 'mobile' ? 'w-[375px]' : 'w-full',
        props.className,
      )}
    >
      <div
        className={clsx(
          'flex justify-center py-3 md:justify-between',
          device === 'mobile' ? 'px-3' : 'px-5',
        )}
      >
        <div className="hidden items-center gap-2 md:flex">
          <div className="h-3 w-3 rounded-full border border-[#CE5347] bg-[#EE6A5F]" />
          <div className="h-3 w-3 rounded-full border border-[#D6A243] bg-[#F5BD4F]" />
          <div className="h-3 w-3 rounded-full border border-[#58A942] bg-[#61C454]" />
        </div>
        <div className="flex w-[60%] items-center justify-center rounded-sm bg-gray-200 py-1.5">
          {_favicon && (
            <img alt="Favicon" className="mr-1 h-6 w-6" src={_favicon} />
          )}
          <FocusableLink
            className="text-sm text-gray-800"
            href={props.openUrl}
            target="_blank"
            data-preview-url="true"
          >
            {props.urlBar}
          </FocusableLink>
        </div>
        <ToggleGroup
          type="single"
          defaultValue="desktop"
          onValueChange={handleDeviceChange}
          className="hidden md:flex"
          data-view-toggle="true"
        >
          <ToggleGroupItem
            value="mobile"
            aria-label="Toggle mobile preview"
            size="sm"
            className="h-fit! px-1.5! py-1!"
          >
            <IconPhone02 className="size-4" />
          </ToggleGroupItem>
          <div className="h-3 w-px bg-gray-300" />
          <ToggleGroupItem
            value="desktop"
            aria-label="Toggle desktop preview"
            size="sm"
            className="h-fit! px-1.5! py-1!"
          >
            <IconMonitor01 className="size-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {props.directRender ? (
        <div className="h-full overflow-y-auto">{props.children}</div>
      ) : (
        <></>
      )}
      {!props.directRender && iframeHead && iframeHead?.length > 0 && (
        <Frame
          key={props.tab}
          data-iframe="true"
          className="w-full flex-1 rounded-b-xl"
          id={props.id}
          title="Preview"
          initialContent={`<!DOCTYPE html>
            <html ${isDark ? 'class="dark"' : ''} data-theme="${themeDefinition.id}">
              <head>
                <meta charset="utf-8">
                <meta name="viewport" content="width=device-width, initial-scale=1">
                ${iframeHead}
              </head>
              <body>
                <div>
                </div>
              </body>
            </html>`}
          head={
            <>
              <link href={themeDefinition.stylesheet} rel="stylesheet" />
              <style id="blog-font">
                {`
                @import url('https://fonts.googleapis.com/css2?family=${defaultFont.replace(
                  ' ',
                  '+',
                )}&display=swap');
              `}
              </style>
              <style id="blog-body">
                {`
                body {
                  font-family: ${getFontFamily(defaultFont)};

                  ${getColorVairables(brandColor)}
                }

                html {
                  font-size: ${fontSize}px;
                }

                ${allCss}
              `}
              </style>
              <IframeStyle tab={props.tab} />
            </>
          }
        >
          <DesignStateIntializer />
          {props.children}
          <IframeJsRunner tab={props.tab} />
        </Frame>
      )}
    </section>
  );
}

function DesignStateIntializer() {
  const [_, setDesign] = useAtom(designAtom);
  const { document: iframeDoc } = useFrame();
  React.useEffect(() => {
    if (!iframeDoc) {
      return;
    }
    setDesign({ enabled: true, iframe: iframeDoc });
  }, [iframeDoc, setDesign]);
  return <></>;
}
